import { MatchPlayerRole, PlatformType } from '@okletsplay/core';
import { MatchStatus } from '@okletsplay/core/src/modules/match/enums/match-status';
import { createSelector } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { MatchEntity } from 'app/interfaces/entity';
import { AppState } from 'app/interfaces/store';
import isEmpty from 'lodash/isEmpty';

import { CurrentMatchState } from './interfaces';

const currentMatchState = (state: AppState): CurrentMatchState =>
  state[EntityStoreName.CurrentMatch];

const matchSelector = createSelector(
  currentMatchState,
  (currentMatch: CurrentMatchState) => currentMatch.entity as MatchEntity
);

const expirationTimeSelector = createSelector(currentMatchState, (state: CurrentMatchState) => {
  if (!state || !state.entity?.status) {
    return 0;
  }

  const entity = state.entity;

  let time: number | undefined;
  switch (entity.status) {
    case MatchStatus.Created:
    case MatchStatus.Pending:
      time = entity.matchSetupSettings?.maxPendingDuration;
      break;
    case MatchStatus.Lobby:
      time = entity.matchSetupSettings?.maxLobbyDuration;
      break;
    case MatchStatus.Live:
      time = entity.matchSetupSettings?.maxLiveDuration;
      break;
    default:
      time = 0;
  }

  return time ?? 0;
});

const ownerSelector = createSelector(matchSelector, (match: MatchEntity) => {
  if (!match || isEmpty(match)) {
    return null;
  }

  return match.players.find((player) => player.role === MatchPlayerRole.Owner);
});

const timeSettingsSelector = createSelector(
  currentMatchState,
  (currentMatch: CurrentMatchState) => {
    const platformsWithTimeSettings = [PlatformType.Lichess, PlatformType.Lidraughts];
    if (!currentMatch?.entity?.game?.platformType) {
      return null;
    }

    if (!platformsWithTimeSettings.includes(currentMatch.entity.game.platformType)) {
      return null;
    }

    if (!currentMatch.entity?.clockLimit && !currentMatch.entity?.clockIncrement) {
      return null;
    }

    const clockLimit = currentMatch.entity?.clockLimit ?? 0;
    const clockIncrement = currentMatch.entity?.clockIncrement ?? 0;
    return { clockLimit, clockIncrement };
  }
);

export const currentMatchSelector = {
  match: matchSelector,
  owner: ownerSelector,
  expirationTime: expirationTimeSelector,
  timeSettings: timeSettingsSelector,
};
